import { useCallback, useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { useRouter } from 'next/router';

import { IVariation, IVariationDetails } from '~/lib/data-contract';
import { useRouteTransition } from '~/shared/utils/route-transition/hooks/useRouteTransition';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';

import { ProductPickerTrack } from './components/ProductPickerTrack/ProductPickerTrack';
import styles from './productPicker.module.css';

import { getColorVariants } from '../../utils/getColorVariants/getColorVariants';
import { getSizeVariants } from '../../utils/getSizeVariants/getSizeVariants';
import { sortColorVariants } from '../../utils/sortColorVariants/sortColorVariants';
import { sortSizeVariants } from '../../utils/sortSizeVariants/sortSizeVariants';
import { variationDetailsToVariation } from '../../utils/variationDetailsToVariation/variationDetailsToVariation';

export type ProductPickerProps = {
	className?: string;
	variation: IVariationDetails;
};

export const ProductPicker = ({ className, variation }: ProductPickerProps) => {
	const router = useRouter();
	const { translate } = useTranslation();
	const [transitioningToUrl, setTransitioningToUrl] = useState<string>();
	const { loading: routeTransitioning } = useRouteTransition();

	useEffect(() => {
		if (!routeTransitioning) {
			setTransitioningToUrl(undefined);
		}
	}, [routeTransitioning]);

	const { variations, sizeAttribute, color, sku } = variation;

	// The product displayed is not a part of the variation list.
	// We want to make sure it is, to be able to show that it is selected.
	const variationsWithCurrent = useMemo(() => {
		return [variationDetailsToVariation(variation), ...variations];
	}, [variation, variations]);

	const colorVariants = useMemo(() => sortColorVariants(getColorVariants(variationsWithCurrent, sizeAttribute)), [sku]);

	const colorVariant = color?.colorId;

	const sizeVariants = useMemo(() => sortSizeVariants(getSizeVariants(variationsWithCurrent, colorVariant)), [colorVariant]);

	const handleVariationClick = useCallback(
		(variation: IVariation) => {
			if (variation.url && router.asPath !== variation.url) {
				setTransitioningToUrl(variation.url);
				router.push(variation.url);
			}
		},
		[router.asPath],
	);

	if (colorVariants.length <= 1 && sizeVariants.length <= 1) {
		return null;
	}

	return (
		<div className={clsx(styles.root, className)}>
			{colorVariants.length > 1 ? (
				<ProductPickerTrack
					transitioningToUrl={transitioningToUrl}
					active={colorVariant}
					variants={colorVariants}
					type="color"
					onVariationClick={handleVariationClick}
					title={translate('product-picker.choose-color')}
				/>
			) : null}
			{sizeVariants.length > 1 ? (
				<ProductPickerTrack
					transitioningToUrl={transitioningToUrl}
					active={sizeAttribute}
					variants={sizeVariants}
					type="size"
					onVariationClick={handleVariationClick}
					title={translate('product-picker.choose-size')}
				/>
			) : null}
		</div>
	);
};
